/**
 * What the support chat is told about the signed-in fan beyond the signed
 * identity: the profile an agent reads beside the conversation.
 *
 * Built only from reads the app already makes for its own screens, so the
 * chat costs no extra call. Every value is something a support agent can act
 * on and nothing secret: no phone number, no birthday, no payment data, no
 * token or claim, and nothing about any other fan. A read that is not in the
 * cache yet leaves its fields out entirely, so a profile value set from an
 * earlier page is not wiped by a page that simply has not loaded it.
 *
 * The provider rules these follow (https://hrc.lt/en/docs/widget/visitor-profile):
 * attribute keys are a-z, 0-9, `_` and `-`, at most 40 characters; text is 1
 * to 255 characters; a date is an ISO string under a key ending in `_at`;
 * `null` removes a key; tags are a-z, 0-9 and `-`, at most 32 characters.
 */
import type {
  typeSupportAttributeValue,
  typeSupportProfile,
  typeSupportTags,
} from "./supportWidget";

/** The fields of profile-api's own-profile read this uses. */
export type typeSupportProfileSource = {
  accountId: string;
  username: string;
  displayName: string;
  photoUrl?: string | null;
  timezone: string;
  isDiscoverable: boolean;
  followedSportIds: string[];
  followedLeagueIds: string[];
  favoriteTeamIds: string[];
  onboardingStep: string;
  isOnboardingComplete: boolean;
  createdAt: string;
};

/** The fan's pools as pool-api's first page of "my pools" lists them. */
export type typeSupportPoolsSource = {
  items: { poolRole: "owner" | "admin" | "member" }[];
  nextCursor?: string | null;
};

/** The fan's global notification switches. */
export type typeSupportNotificationsSource = {
  isEmailKillSwitchOn: boolean;
  activePresetId?: string | null;
};

/** Always on: this surface is the web app. The phone apps would add their own. */
export const SUPPORT_TAG_WEB = "web";
/** On while the fan is in at least one current pool. */
export const SUPPORT_TAG_HAS_POOLS = "has-pools";
/** On while the fan owns at least one current pool. */
export const SUPPORT_TAG_POOL_OWNER = "pool-owner";

const NAME_MAX = 120;
const AVATAR_URL_MAX = 500;
const TEXT_MAX = 255;

/** A list as one comma separated text, cut at a whole entry under 255. */
function joinedList(values: string[]): string | null {
  let joined = "";
  for (const value of values) {
    const next = joined === "" ? value : `${joined},${value}`;
    if (next.length > TEXT_MAX) break;
    joined = next;
  }
  return joined === "" ? null : joined;
}

function avatarUrl(photoUrl: string | null | undefined): string | null {
  if (typeof photoUrl !== "string") return null;
  if (!photoUrl.startsWith("https://")) return null;
  return photoUrl.length <= AVATAR_URL_MAX ? photoUrl : null;
}

export function buildSupportProfile({
  profile,
  pools,
  notifications,
}: {
  profile: typeSupportProfileSource | null;
  pools: typeSupportPoolsSource | null;
  notifications: typeSupportNotificationsSource | null;
}): { profile: typeSupportProfile; tags: typeSupportTags } {
  const attributes: Record<string, typeSupportAttributeValue> = {
    platform: "web",
  };
  const result: typeSupportProfile = {};
  const tags: typeSupportTags = { add: [SUPPORT_TAG_WEB], remove: [] };

  if (profile !== null) {
    const name = profile.displayName.trim() || profile.username.trim();
    if (name !== "") result.name = name.slice(0, NAME_MAX);
    result.avatarUrl = avatarUrl(profile.photoUrl);
    attributes.username = profile.username.slice(0, TEXT_MAX) || null;
    attributes.joined_at = profile.createdAt;
    attributes.timezone = profile.timezone.slice(0, TEXT_MAX) || null;
    attributes.onboarding_complete = profile.isOnboardingComplete;
    attributes.onboarding_step = profile.onboardingStep;
    attributes.is_discoverable = profile.isDiscoverable;
    attributes.followed_sports = joinedList(profile.followedSportIds);
    attributes.followed_leagues = joinedList(profile.followedLeagueIds);
    attributes.favorite_team_count = profile.favoriteTeamIds.length;
  }

  if (pools !== null) {
    const ownedCount = pools.items.filter(
      (item) => item.poolRole === "owner",
    ).length;
    // A first page that is not the whole list counts only that page, so the
    // counts are sent only when the list is complete. The tags hold either way:
    // one pool on the first page is a pool.
    const isComplete =
      pools.nextCursor === undefined || pools.nextCursor === null;
    if (isComplete) {
      attributes.pool_count = pools.items.length;
      attributes.owned_pool_count = ownedCount;
    }
    (pools.items.length > 0 ? tags.add : tags.remove).push(
      SUPPORT_TAG_HAS_POOLS,
    );
    (ownedCount > 0 ? tags.add : tags.remove).push(SUPPORT_TAG_POOL_OWNER);
  }

  if (notifications !== null) {
    attributes.email_alerts_off = notifications.isEmailKillSwitchOn;
    attributes.notification_preset = notifications.activePresetId ?? null;
  }

  result.attributes = attributes;
  return { profile: result, tags };
}
